// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useCallback, useRef } from "react";

/**
 * Out-of-order response guard for a list fetch.
 *
 * Change the sort, then change it again before the first request lands, and
 * the SLOWER earlier response would otherwise resolve last and overwrite the
 * newer one — leaving rows that contradict the header arrow the user is
 * looking at. Filters make this easy to hit: unlike the debounced search box,
 * a facet or header click fires immediately, so two requests are routinely in
 * flight at once.
 *
 * `AbortController` is deliberately NOT used: the api layer here shares one
 * axios instance whose interceptors treat a cancelled request as a network
 * error, and every list page already has a `catch` that toasts "Failed to
 * load". Ignoring a stale RESULT is quieter than cancelling a stale REQUEST.
 *
 * Usage — take a ticket before awaiting, then check it before touching state:
 *
 * ```ts
 * const beginRequest = useLatestRequest();
 * const load = useCallback(async () => {
 *   const isCurrent = beginRequest();
 *   try {
 *     const res = await api.getAll(params);
 *     if (!isCurrent()) return;          // a newer load already started
 *     setRows(res.rows);
 *   } catch (e) {
 *     if (!isCurrent()) return;          // don't toast for a superseded call
 *     …
 *   } finally {
 *     if (isCurrent()) setLoading(false); // an unguarded finally would clear
 *   }                                     // the spinner the NEWER load set
 * }, [params, beginRequest]);
 * ```
 *
 * The guarded `finally` is the part that is easy to get wrong: `return`ing
 * from `try` still runs `finally`, so a superseded request would otherwise
 * turn off the spinner belonging to the request that superseded it.
 */
export function useLatestRequest(): () => () => boolean {
  const latest = useRef(0);

  return useCallback(() => {
    const mine = ++latest.current;
    return () => mine === latest.current;
  }, []);
}
